import React, { useState, useEffect } from 'react';
import { Icon } from './Icon';

interface CookieConsentProps {
  onNavigatePrivacy?: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onNavigatePrivacy }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [view, setView] = useState<'summary' | 'preferences'>('summary');
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: true,
    ads: true
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, ads: true };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const togglePreference = (key: 'analytics' | 'ads') => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 transition-all duration-500 ease-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
      <div className={`max-w-4xl mx-auto bg-[#0f172a]/95 backdrop-blur-md border border-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] text-slate-200 transition-all duration-300 ${view === 'summary' ? 'p-4 md:p-5' : 'p-5 md:p-6'}`}>

        {view === 'summary' ? (
          /* SUMMARY VIEW */
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-grow">
              <h3 className="text-sm font-bold text-white mb-0.5">
                Nós usamos cookies
              </h3>
              <p className="text-slate-400 text-[11px] leading-relaxed max-w-2xl">
                Utilizamos cookies para melhorar sua experiência, analisar o uso do site e exibir anúncios relevantes. Ao continuar navegando, você concorda com o uso de cookies, conforme nossa <button onClick={onNavigatePrivacy} className="text-slate-300 underline hover:text-white transition-colors">Política de Privacidade</button>.
              </p>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto shrink-0 mt-1 md:mt-0">
              <button
                onClick={handleAcceptAll}
                className="flex-1 md:flex-none px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] rounded-xl font-bold transition-all text-[11px] active:scale-95 shadow-lg shadow-emerald-500/10"
              >
                Aceitar
              </button>
              <button
                onClick={() => setView('preferences')}
                className="flex-1 md:flex-none px-5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-400 rounded-xl font-bold transition-all text-[11px] active:scale-95"
              >
                Personalizar
              </button>
            </div>
          </div>
        ) : (
          /* PREFERENCES VIEW - Ultra Compact */
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <h3 className="text-sm font-bold text-white">
                Personalizar cookies
              </h3>
              <button onClick={() => setView('summary')} className="text-slate-500 hover:text-white transition-colors">
                <Icon name="compress" className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-1">
              {/* Essential */}
              <div className="flex items-center gap-3 p-2 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="w-7 h-7 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="check-circle" className="w-3.5 h-3.5 text-emerald-500" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold text-white text-[10px] leading-tight">Essenciais</h4>
                  <span className="text-[8px] uppercase font-black text-emerald-500 tracking-tighter">Ativo</span>
                </div>
              </div>

              {/* Analytics */}
              <div
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${preferences.analytics ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-slate-800/30 border border-slate-700/50'}`}
                onClick={() => togglePreference('analytics')}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${preferences.analytics ? 'bg-emerald-500/20' : 'bg-slate-900 border border-slate-700'}`}>
                  {preferences.analytics ? (
                    <Icon name="check-circle" className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                  )}
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold text-white text-[10px] leading-tight">Análise</h4>
                  <span className={`text-[8px] uppercase font-black tracking-tighter ${preferences.analytics ? 'text-emerald-500' : 'text-slate-500'}`}>
                    {preferences.analytics ? 'Ativado' : 'Desativado'}
                  </span>
                </div>
              </div>

              {/* Ads */}
              <div
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${preferences.ads ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-slate-800/30 border border-slate-700/50'}`}
                onClick={() => togglePreference('ads')}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${preferences.ads ? 'bg-emerald-500/20' : 'bg-slate-900 border border-slate-700'}`}>
                  {preferences.ads ? (
                    <Icon name="check-circle" className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                  )}
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold text-white text-[10px] leading-tight">Anúncios</h4>
                  <span className={`text-[8px] uppercase font-black tracking-tighter ${preferences.ads ? 'text-emerald-500' : 'text-slate-500'}`}>
                    {preferences.ads ? 'Ativado' : 'Desativado'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800/50">
              <button
                onClick={() => setView('summary')}
                className="px-4 py-2 text-slate-400 hover:text-white transition-all text-[10px] font-bold"
              >
                Voltar
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2 bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] rounded-xl font-black transition-all text-[10px] active:scale-95 shadow-lg shadow-emerald-500/10"
              >
                Salvar preferências
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
